import React, { useEffect, useState } from 'react';
import { api, apiErr } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { fmtDateTimeMX } from '@/lib/format';
import { useAuth } from '@/context/AuthContext';
import TwoFactorSetup from '@/components/TwoFactorSetup';
import SessionsPanel from '@/components/SessionsPanel';
import { NipChangeDialog, NipSetupDialog } from '@/components/NipDialogs';
import { ShieldCheck, ShieldOff, RotateCcw, KeyRound } from 'lucide-react';

export default function Settings() {
  const { user } = useAuth();
  const [activities, setActivities] = useState([]);
  const [rates, setRates] = useState({});
  const [reasons, setReasons] = useState({});
  const [history, setHistory] = useState([]);
  const [pw, setPw] = useState({ old: '', new1: '', new2: '' });
  const [integ, setInteg] = useState(null);
  const [twoFA, setTwoFA] = useState({ enabled: false, recovery_codes_remaining: 0 });
  const [twoFAOpen, setTwoFAOpen] = useState(false);
  const [disable2FA, setDisable2FA] = useState({ open: false, password: '', code: '' });
  const [regen, setRegen] = useState({ open: false, password: '', code: '', codes: [] });
  const [nip, setNip] = useState({ enabled: false });
  const [nipSetupOpen, setNipSetupOpen] = useState(false);
  const [nipChangeOpen, setNipChangeOpen] = useState(false);

  const load = async () => {
    const a = await api.get('/config/activities');
    setActivities(a.data);
    const map = {}; a.data.forEach(x => { map[x.id] = String(x.default_iva_rate); });
    setRates(map);
    const h = await api.get('/config/iva-history');
    setHistory(h.data);
    const i = await api.get('/integrations/status');
    setInteg(i.data);
    try {
      const t = await api.get('/auth/2fa/status');
      setTwoFA(t.data);
    } catch { /* ignore */ }
    try {
      const n = await api.get('/auth/nip/status');
      setNip(n.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const saveRate = async (id) => {
    try {
      await api.put(`/config/activities/${id}/iva-rate`, { rate: parseFloat(rates[id] || 0), reason: reasons[id] || '' });
      toast.success('Tasa actualizada');
      setReasons(r => ({ ...r, [id]: '' }));
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const changePassword = async () => {
    if (pw.new1 !== pw.new2) { toast.error('Las contraseñas nuevas no coinciden'); return; }
    if (pw.new1.length < 10) { toast.error('Mínimo 10 caracteres'); return; }
    try {
      await api.post('/auth/change-password', { old_password: pw.old, new_password: pw.new1 });
      toast.success('Contraseña actualizada');
      setPw({ old: '', new1: '', new2: '' });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const backup = async () => {
    try {
      const r = await api.get('/backup', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `resico-backup-${new Date().toISOString().slice(0,10)}.json`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-display text-2xl font-bold tracking-tight">Configuración fiscal</h2>
        <p className="text-xs text-muted-foreground mt-0.5">Tasas de IVA, seguridad e integraciones</p>
      </div>

      <Card className="p-4 border-border rounded-xl">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3">Tasas de IVA por actividad</p>
        <div className="space-y-4">
          {activities.map(a => (
            <div key={a.id} className="space-y-2" data-testid={`activity-${a.id}`}>
              <p className="text-sm font-medium">{a.name}</p>
              <div className="flex gap-2">
                <Input type="number" step="0.01" inputMode="decimal" data-testid={`rate-${a.id}`} className="h-11 num" value={rates[a.id] ?? ''} onChange={(e) => setRates(r => ({ ...r, [a.id]: e.target.value }))}/>
                <Button data-testid={`save-rate-${a.id}`} onClick={() => saveRate(a.id)} variant="outline" className="h-11 rounded-full px-4">Guardar</Button>
              </div>
              <Input data-testid={`reason-${a.id}`} placeholder="Motivo del cambio (opcional)" className="h-10 text-sm" value={reasons[a.id] || ''} onChange={(e) => setReasons(r => ({ ...r, [a.id]: e.target.value }))}/>
              <p className="text-[11px] text-muted-foreground">Actual: {(a.default_iva_rate * 100).toFixed(2)}%</p>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-4 border-border rounded-xl">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Historial de cambios IVA</p>
        {history.length === 0 ? <p className="text-sm text-muted-foreground">Sin cambios registrados.</p> : (
          <div className="space-y-2">
            {history.map(h => (
              <div key={h.id} className="text-xs border border-border rounded-md p-2">
                <div className="flex justify-between font-medium">
                  <span>{activities.find(a => a.id === h.activity_id)?.name || h.activity_id}</span>
                  <span className="num">{(h.previous_rate*100).toFixed(2)}% → {(h.new_rate*100).toFixed(2)}%</span>
                </div>
                <p className="text-muted-foreground">{fmtDateTimeMX(h.changed_at)} · {h.changed_by}</p>
                {h.reason && <p className="text-muted-foreground italic">&ldquo;{h.reason}&rdquo;</p>}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-4 border-border rounded-xl">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3">Autenticación de dos factores (2FA)</p>
        {twoFA.enabled ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-primary">
              <ShieldCheck className="h-5 w-5"/>
              <p className="text-sm font-semibold" data-testid="twofa-status">2FA activo</p>
            </div>
            <p className="text-xs text-muted-foreground">Códigos de recuperación disponibles: <span className="num font-semibold text-foreground" data-testid="twofa-recovery-count">{twoFA.recovery_codes_remaining}</span> / 10</p>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" data-testid="twofa-regen-open" onClick={() => setRegen({ open: true, password: '', code: '', codes: [] })} className="h-10 rounded-full text-xs">
                <RotateCcw className="h-3.5 w-3.5 mr-1"/> Regenerar códigos
              </Button>
              <Button variant="outline" data-testid="twofa-disable-open" onClick={() => setDisable2FA({ open: true, password: '', code: '' })} className="h-10 rounded-full text-xs text-destructive border-destructive/30">
                <ShieldOff className="h-3.5 w-3.5 mr-1"/> Desactivar 2FA
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Añade una segunda capa de seguridad. Compatible con Microsoft Authenticator, Authy y Google Authenticator.</p>
            <Button data-testid="twofa-setup-open" onClick={() => setTwoFAOpen(true)} className="w-full h-11 rounded-full">
              <ShieldCheck className="h-4 w-4 mr-2"/> Activar 2FA
            </Button>
          </div>
        )}
      </Card>

      <TwoFactorSetup open={twoFAOpen} onOpenChange={setTwoFAOpen} onEnabled={load}/>

      <SessionsPanel/>

      <Card className="p-4 border-border rounded-xl" data-testid="nip-panel">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3">NIP para periodos declarados</p>
        {nip.enabled ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-primary">
              <KeyRound className="h-5 w-5"/>
              <p className="text-sm font-semibold" data-testid="nip-status">NIP configurado</p>
            </div>
            <p className="text-xs text-muted-foreground">Se solicitará para modificar, agregar o eliminar ventas, gastos o depósitos en periodos ya presentados al SAT.</p>
            <Button data-testid="btn-change-nip" variant="outline" onClick={() => setNipChangeOpen(true)} className="w-full h-11 rounded-full">
              <RotateCcw className="h-4 w-4 mr-2"/> Cambiar NIP
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Crea un NIP para desbloquear ediciones de periodos ya presentados al SAT.</p>
            <Button data-testid="btn-setup-nip" onClick={() => setNipSetupOpen(true)} className="w-full h-11 rounded-full">
              <KeyRound className="h-4 w-4 mr-2"/> Crear NIP
            </Button>
          </div>
        )}
      </Card>

      <NipSetupDialog open={nipSetupOpen} onOpenChange={setNipSetupOpen} onDone={load}/>
      <NipChangeDialog open={nipChangeOpen} onOpenChange={setNipChangeOpen} twoFAEnabled={twoFA.enabled}/>

      {/* Disable 2FA modal */}
      {disable2FA.open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="disable-2fa-modal">
          <div className="bg-card rounded-xl border border-border p-6 max-w-md w-full space-y-4">
            <div>
              <p className="font-display font-bold text-lg">Desactivar 2FA</p>
              <p className="text-xs text-muted-foreground">Se requiere tu contraseña actual + un código TOTP válido.</p>
            </div>
            <Input type="password" placeholder="Contraseña actual" data-testid="disable-2fa-password" className="h-11" value={disable2FA.password} onChange={(e) => setDisable2FA(s => ({ ...s, password: e.target.value }))}/>
            <Input inputMode="numeric" maxLength={6} placeholder="Código TOTP" data-testid="disable-2fa-code" className="h-11 text-center font-mono-num text-lg tracking-[0.4em]" value={disable2FA.code} onChange={(e) => setDisable2FA(s => ({ ...s, code: e.target.value.replace(/\D/g, '') }))}/>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setDisable2FA({ open: false, password: '', code: '' })} className="flex-1 h-11 rounded-full">Cancelar</Button>
              <Button data-testid="disable-2fa-confirm" onClick={async () => {
                try {
                  await api.post('/auth/2fa/disable', { password: disable2FA.password, code: disable2FA.code });
                  toast.success('2FA desactivado');
                  setDisable2FA({ open: false, password: '', code: '' });
                  load();
                } catch (e) { toast.error(apiErr(e)); }
              }} className="flex-1 h-11 rounded-full">Desactivar</Button>
            </div>
          </div>
        </div>
      )}

      {/* Regenerate recovery codes modal */}
      {regen.open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="regen-modal">
          <div className="bg-card rounded-xl border border-border p-6 max-w-md w-full space-y-4">
            <div>
              <p className="font-display font-bold text-lg">Regenerar códigos de recuperación</p>
              <p className="text-xs text-muted-foreground">Los códigos anteriores dejarán de funcionar.</p>
            </div>
            {regen.codes.length === 0 ? (
              <>
                <Input type="password" placeholder="Contraseña actual" data-testid="regen-password" className="h-11" value={regen.password} onChange={(e) => setRegen(s => ({ ...s, password: e.target.value }))}/>
                <Input inputMode="numeric" maxLength={6} placeholder="Código TOTP" data-testid="regen-code" className="h-11 text-center font-mono-num text-lg tracking-[0.4em]" value={regen.code} onChange={(e) => setRegen(s => ({ ...s, code: e.target.value.replace(/\D/g, '') }))}/>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setRegen({ open: false, password: '', code: '', codes: [] })} className="flex-1 h-11 rounded-full">Cancelar</Button>
                  <Button data-testid="regen-confirm" onClick={async () => {
                    try {
                      const r = await api.post('/auth/2fa/regenerate-recovery-codes', { password: regen.password, code: regen.code });
                      setRegen(s => ({ ...s, codes: r.data.recovery_codes }));
                      load();
                    } catch (e) { toast.error(apiErr(e)); }
                  }} className="flex-1 h-11 rounded-full">Regenerar</Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-xs text-muted-foreground">Guarda estos 10 códigos en un lugar seguro:</p>
                <div className="grid grid-cols-2 gap-2 rounded-lg border border-border p-3 bg-secondary/40 font-mono-num text-sm" data-testid="regen-codes">
                  {regen.codes.map((c, i) => (<div key={i}>{c}</div>))}
                </div>
                <Button onClick={() => setRegen({ open: false, password: '', code: '', codes: [] })} className="w-full h-11 rounded-full">Listo</Button>
              </>
            )}
          </div>
        </div>
      )}

      <Card className="p-4 border-border rounded-xl">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3">Cambiar contraseña</p>
        <div className="space-y-2">
          <Input type="password" placeholder="Contraseña actual" data-testid="pw-old" className="h-11" value={pw.old} onChange={(e) => setPw(p => ({ ...p, old: e.target.value }))}/>
          <Input type="password" placeholder="Nueva contraseña (mín 10)" data-testid="pw-new1" className="h-11" value={pw.new1} onChange={(e) => setPw(p => ({ ...p, new1: e.target.value }))}/>
          <Input type="password" placeholder="Confirmar nueva" data-testid="pw-new2" className="h-11" value={pw.new2} onChange={(e) => setPw(p => ({ ...p, new2: e.target.value }))}/>
          <Button data-testid="pw-save" onClick={changePassword} className="w-full h-11 rounded-full">Actualizar contraseña</Button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-3">Sesión expira por inactividad (20 min). No se guardan contraseñas del SAT ni e.firma.</p>
      </Card>

      <Card className="p-4 border-border rounded-xl">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3">Respaldos</p>
        <Button data-testid="btn-backup" variant="outline" onClick={backup} className="w-full h-11 rounded-full">Descargar respaldo JSON</Button>
      </Card>

      <Card className="p-4 border-border rounded-xl">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3">Integraciones futuras</p>
        {integ && (
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between rounded-md border border-border p-2">
              <div>
                <p className="font-medium">CFDI vía PAC</p>
                <p className="text-xs text-muted-foreground">{integ.cfdi_pac.note}</p>
              </div>
              <span className="text-[10px] uppercase tracking-widest bg-secondary rounded px-2 py-1">Desactivado</span>
            </div>
            <div className="flex items-center justify-between rounded-md border border-border p-2">
              <div>
                <p className="font-medium">Conciliación bancaria</p>
                <p className="text-xs text-muted-foreground">{integ.bank_reconciliation.note}</p>
              </div>
              <span className="text-[10px] uppercase tracking-widest bg-secondary rounded px-2 py-1">Desactivado</span>
            </div>
          </div>
        )}
      </Card>

      <p className="text-[11px] text-muted-foreground pt-2">Cuenta: <span className="font-mono">{user?.email}</span></p>
    </div>
  );
}
